// Web Bluetooth GATT transport for Star TSP143IV-BI.
// Targets Chrome/Edge desktop with the Web Bluetooth API.
// Browsers that don't support Web Bluetooth throw 'web_bluetooth_unsupported'.

import { POS_SUPPORTED_PRINTERS } from './allowlist';

const STORAGE_KEY = 'pos.printer.deviceId';

// Star TSP series GATT service + characteristic UUIDs (from Star SDK docs).
const SERVICE_UUID = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
const CHAR_UUID = '49535343-8841-43f4-a8d4-ecbe34729bb3';

/** Maximum bytes per BLE write (minimum guaranteed MTU). */
const BLE_CHUNK = 20;

// ---------------------------------------------------------------------------
// Types for the experimental Web Bluetooth API surface
// ---------------------------------------------------------------------------

interface BluetoothRemoteGATTCharacteristic {
  writeValueWithoutResponse(value: ArrayBuffer): Promise<void>;
}

interface BluetoothRemoteGATTService {
  getCharacteristic(uuid: string): Promise<BluetoothRemoteGATTCharacteristic>;
}

interface BluetoothRemoteGATTServer {
  connected: boolean;
  connect(): Promise<BluetoothRemoteGATTServer>;
  getPrimaryService(uuid: string): Promise<BluetoothRemoteGATTService>;
  disconnect(): void;
}

interface BluetoothDevice {
  id: string;
  name?: string;
  gatt?: BluetoothRemoteGATTServer;
}

interface RequestDeviceOptions {
  filters: Array<{ namePrefix?: string }>;
  optionalServices: string[];
}

interface Bluetooth {
  requestDevice(options: RequestDeviceOptions): Promise<BluetoothDevice>;
  // getDevices() is experimental and not universally typed — defensive cast at call site.
  getDevices?(): Promise<BluetoothDevice[]>;
}

declare global {
  interface Navigator {
    bluetooth?: Bluetooth;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function assertBluetooth(): Bluetooth {
  if (typeof navigator === 'undefined' || !navigator.bluetooth) {
    throw new Error('web_bluetooth_unsupported');
  }
  return navigator.bluetooth;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function pairPrinter(): Promise<{ deviceId: string; name: string }> {
  const bt = assertBluetooth();

  const device = await bt.requestDevice({
    filters: POS_SUPPORTED_PRINTERS.map((p) => ({ namePrefix: p.name_prefix })),
    optionalServices: [SERVICE_UUID],
  });

  localStorage.setItem(STORAGE_KEY, device.id);

  return { deviceId: device.id, name: device.name ?? device.id };
}

export async function unpairPrinter(): Promise<void> {
  localStorage.removeItem(STORAGE_KEY);
}

export async function getPairedPrinter(): Promise<{ deviceId: string; name: string } | null> {
  const storedId = localStorage.getItem(STORAGE_KEY);
  if (!storedId) return null;

  // assertBluetooth returns without throwing here — if BT is unsupported we simply
  // return null (the device can't be reached anyway).
  if (typeof navigator === 'undefined' || !navigator.bluetooth) return null;

  const bt = navigator.bluetooth;

  try {
    // getDevices() returns only previously-permitted devices without a user prompt.
    if (typeof bt.getDevices !== 'function') return null;

    const devices = await bt.getDevices();
    const matched = devices.find((d) => d.id === storedId);
    if (!matched) return null;

    return { deviceId: matched.id, name: matched.name ?? matched.id };
  } catch {
    return null;
  }
}

export async function printBytes(data: Uint8Array): Promise<void> {
  const bt = assertBluetooth();

  const storedId = localStorage.getItem(STORAGE_KEY);
  if (!storedId) throw new Error('printer_not_paired');

  // Get the permitted device list; throw if device is gone.
  let device: BluetoothDevice | undefined;
  try {
    if (typeof bt.getDevices !== 'function') throw new Error('printer_not_paired');
    const devices = await bt.getDevices();
    device = devices.find((d) => d.id === storedId);
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : '';
    if (msg === 'printer_not_paired') throw err;
    throw new Error('printer_not_paired');
  }

  if (!device) throw new Error('printer_not_paired');
  if (!device.gatt) throw new Error('printer_not_paired');

  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(SERVICE_UUID);
  const char = await service.getCharacteristic(CHAR_UUID);

  // Write in BLE_CHUNK-byte chunks.
  for (let offset = 0; offset < data.length; offset += BLE_CHUNK) {
    const chunk = data.slice(offset, offset + BLE_CHUNK);
    await char.writeValueWithoutResponse(chunk.buffer);
  }

  server.disconnect();
}

export async function kickDrawer(): Promise<void> {
  // ESC/POS cash drawer kick: ESC p 0 50 50 (Star/Epson compatible)
  await printBytes(new Uint8Array([0x1b, 0x70, 0x00, 0x32, 0x32]));
}
